import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Container,
  Divider,
  Grid,
  Avatar,
  TextField,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardActionArea,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ImageList,
  ImageListItem,
  Tooltip,
} from '@mui/material';
import { Person, Edit, Save, PhotoCamera, Close } from '@mui/icons-material';
import {
  useUserServiceGetApiUserMe,
  useUserServicePutApiUserMe,
  // useUserAvatarControllerServiceGetApiV1UserAvatarsAvailable,
  // useUserAvatarControllerServicePostApiV1UserAvatarsSelect,
  // useUserAvatarControllerServiceGetApiV1UserAvatarsByUserId
} from '../api/generated/queries/queries';
import { UserDTO /*, AvatarOptionDTO*/ } from '../api/generated/requests/types.gen';
import { AuthService } from '../services/authService';
// import { AvatarCacheService } from '../services/avatarCacheService';
import WidgetCard from '../components/DashboardWidgets/WidgetCard';


const MyAccountPage: React.FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  // const [avatarPreviewOpen, setAvatarPreviewOpen] = useState(false);
  // const [previewAvatar, setPreviewAvatar] = useState<AvatarOptionDTO | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Get current user info
  const keycloakUserInfo = AuthService.getUserInfo();
  const currentUserId = keycloakUserInfo.sub;

  // Fetch current user data
  const { data: userData, isLoading, error, refetch } = useUserServiceGetApiUserMe();

  // Fetch available avatars
  // const { data: availableAvatars, isLoading: avatarsLoading } = useUserAvatarControllerServiceGetApiV1UserAvatarsAvailable();

  // Fetch current user avatar
  // const { data: currentUserAvatar, refetch: refetchUserAvatar } = useUserAvatarControllerServiceGetApiV1UserAvatarsByUserId({
  //   userId: currentUserId || ''
  // }, undefined, {
  //   enabled: !!currentUserId
  // });

  // Form state
  const [formData, setFormData] = useState<UserDTO>({
    firstName: '',
    lastName: '',
    email: '',
    login: '',
  });

  // Update mutations
  const updateUserMutation = useUserServicePutApiUserMe();
  // const selectAvatarMutation = useUserAvatarControllerServicePostApiV1UserAvatarsSelect();

  // Initialize form data when user data is loaded
  useEffect(() => {
    if (userData) {
      setFormData({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        login: userData.login || '',
      });
    }
  }, [userData]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Toggle edit mode
  const toggleEditMode = () => {
    setIsEditing(!isEditing);
  };

  // Handle cancel edit mode
  const handleCancelEdit = () => {
    // Reset form data to original user data
    if (userData) {
      setFormData({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        login: userData.login || '',
      });
    }
    setIsEditing(false);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await updateUserMutation.mutateAsync({
        requestBody: formData
      });

      setSnackbar({
        open: true,
        message: 'Profil został zaktualizowany pomyślnie',
        severity: 'success',
      });

      setIsEditing(false);
      refetch(); // Refresh user data
    } catch (error) {
      console.error('Error updating profile:', error);
      setSnackbar({
        open: true,
        message: 'Wystąpił błąd podczas aktualizacji profilu',
        severity: 'error',
      });
    }
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };


  // Handle avatar selection
  // const handleAvatarSelect = async (avatar: AvatarOptionDTO) => {
  //   if (!avatar.fileName) return;

  //   try {
  //     await selectAvatarMutation.mutateAsync({
  //       requestBody: {
  //         avatarName: avatar.fileName
  //       }
  //     });

  //     setSnackbar({
  //       open: true,
  //       message: 'Avatar został zaktualizowany pomyślnie',
  //       severity: 'success',
  //     });

  //     // Refresh user avatar data
  //     refetchUserAvatar();
      
  //     // Refresh avatar cache for current user
  //     if (currentUserId) {
  //       AvatarCacheService.removeCachedAvatar(currentUserId);
  //       // Preload the new avatar
  //       await AvatarCacheService.getAvatar(currentUserId);
  //     }
  //   } catch (error) {
  //     console.error('Error selecting avatar:', error);
  //     setSnackbar({
  //       open: true,
  //       message: 'Wystąpił błąd podczas aktualizacji avatara',
  //       severity: 'error',
  //     });
  //   }
  // };

  // Handle avatar preview
  // const handleAvatarPreview = (avatar: AvatarOptionDTO) => {
  //   setPreviewAvatar(avatar);
  //   setAvatarPreviewOpen(true);
  // };

  // Close avatar preview
  // const handleCloseAvatarPreview = () => {
  //   setAvatarPreviewOpen(false);
  //   setPreviewAvatar(null);
  // };

  // Get user initials
  const getUserInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return `${first}${last}`;
  };

  if (isLoading) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">
          Wystąpił błąd podczas ładowania danych użytkownika. Proszę odświeżyć stronę.
        </Alert>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>

      <Grid container spacing={3}>
        {/* Main User Information and Avatar Selection Card */}
        <Grid size={{ xs: 12 }}>
          <WidgetCard>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* <Tooltip title={`${userData?.firstName} ${userData?.lastName}`}>
                  {currentUserAvatar?.avatarUrl ? (
                    <Avatar 
                      src={currentUserAvatar.avatarUrl}
                      sx={{ 
                        width: 40, 
                        height: 40,
                        border: '2px solid',
                        borderColor: 'primary.main',
                      }}
                    />
                  ) : (
                    <Avatar sx={{ 
                      bgcolor: 'primary.main',
                      background: 'linear-gradient(to right, #06b6d4, #2563eb)',
                      width: 40,
                      height: 40
                    }}>
                      {getUserInitials(userData?.firstName, userData?.lastName) || <Person />}
                    </Avatar>
                  )}
                </Tooltip> */}
                <Typography variant="h6" fontWeight={600}>
                  Informacje o profilu
                </Typography>
              </Box>
              {isEditing ? (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    startIcon={<Save />}
                    onClick={handleSubmit}
                    sx={{
                      background: 'linear-gradient(to right, #059669, #16a34a)',
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      borderRadius: '9999px',
                      fontWeight: 400,
                      textTransform: 'none',
                      boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.2)',
                      '&:hover': {
                        background: 'linear-gradient(to right, #047857, #15803d)',
                      },
                    }}
                  >
                    Zapisz
                  </Button>
                  <Button 
                    onClick={handleCancelEdit}
                    sx={{
                      background: 'linear-gradient(to right, #6b7280, #4b5563)',
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      borderRadius: '9999px',
                      fontWeight: 400,
                      textTransform: 'none',
                      boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.2)',
                      '&:hover': {
                        background: 'linear-gradient(to right, #4b5563, #374151)',
                      },
                    }}
                  >
                    Anuluj
                  </Button>
                </Box>
              ) : (
                <Button 
                  startIcon={<Edit />}
                  onClick={toggleEditMode}
                  sx={{
                    background: 'linear-gradient(to right, #06b6d4, #2563eb)',
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    borderRadius: '9999px',
                    fontWeight: 400,
                    textTransform: 'none',
                    boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.2)',
                    '&:hover': {
                      background: 'linear-gradient(to right, #059dc5, #1e4ed8)',
                    },
                  }}
                >
                  Edytuj
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 3 }} />
            
            {/* User Data Section */}
            <form onSubmit={handleSubmit}>
              {isEditing ? (
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Imię"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Nazwisko"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      fullWidth
                      variant="outlined"
                      size="small"
                      type="email"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Login"
                      name="login"
                      value={formData.login}
                      onChange={handleInputChange}
                      fullWidth
                      variant="outlined"
                      size="small"
                      disabled
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Imię i nazwisko:</strong> {userData?.firstName} {userData?.lastName}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Email:</strong> {userData?.email}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Login:</strong> {userData?.login}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Stanowisko:</strong> {keycloakUserInfo.roles.includes('api-admin') ? 'Administrator' : 'Użytkownik'}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </form>

            {/* <Divider sx={{ mb: 3 }} />

            Avatar Selection Section
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar sx={{ 
                bgcolor: 'primary.main',
                background: 'linear-gradient(to right, #10b981, #059669)',
                width: 40,
                height: 40
              }}>
                <PhotoCamera />
              </Avatar>
              <Typography variant="h6" fontWeight={600}>
                Wybierz avatar
              </Typography>
            </Box>

            <Grid container spacing={3}>
              Current Avatar Display
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Aktualny avatar
                  </Typography>
                  {currentUserAvatar?.avatarUrl ? (
                    <Avatar
                      src={currentUserAvatar.avatarUrl}
                      sx={{
                        width: 120,
                        height: 120,
                        border: '3px solid',
                        borderColor: 'primary.main',
                      }}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: 120,
                        height: 120,
                        bgcolor: 'primary.main',
                        fontSize: '2rem',
                        border: '3px solid',
                        borderColor: 'primary.main',
                      }}
                    >
                      {getUserInitials(userData?.firstName, userData?.lastName)}
                    </Avatar>
                  )}
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    {currentUserAvatar?.selectedAvatarName 
                      ? 'Avatar został wybrany'
                      : 'Nie wybrano avatara - wyświetlane są inicjały'
                    }
                  </Typography>
                </Box>
              </Grid>

              Avatar Selection Grid
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  Dostępne avatary
                </Typography>
                
                {avatarsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : availableAvatars && availableAvatars.length > 0 ? (
                  <ImageList cols={4} gap={16} sx={{ maxHeight: 400 }}>
                    {availableAvatars.map((avatar, index) => (
                      <ImageListItem key={index}>
                        <Card
                          sx={{
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            border: currentUserAvatar?.selectedAvatarName === avatar.fileName 
                              ? '3px solid' 
                              : '2px solid transparent',
                            borderColor: currentUserAvatar?.selectedAvatarName === avatar.fileName 
                              ? 'primary.main' 
                              : 'transparent',
                            '&:hover': {
                              transform: 'scale(1.05)',
                              boxShadow: 3,
                            }
                          }}
                        >
                          <CardActionArea
                            onClick={() => handleAvatarPreview(avatar)}
                          >
                            <Avatar
                              src={avatar.downloadUrl}
                              sx={{
                                width: '100%',
                                height: 80,
                                borderRadius: '50%',
                              }}
                            />
                          </CardActionArea>
                        </Card>
                      </ImageListItem>
                    ))}
                  </ImageList>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Brak dostępnych avatarów
                    </Typography>
                  </Box>
                )}
                
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Kliknij na avatar, aby zobaczyć podgląd i wybrać go.
                </Typography>
              </Grid>
            </Grid> */}
          </WidgetCard>
        </Grid>

        {/* Commented out dark theme tile as requested */}
        {/* 
        <Grid size={{ xs: 12, md: 4 }}>
          <WidgetCard>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar sx={{ 
                bgcolor: 'primary.main',
                background: 'linear-gradient(to right, #8b5cf6, #a855f7)',
                width: 40,
                height: 40
              }}>
                <Palette />
              </Avatar>
              <Typography variant="h6" fontWeight={600}>
                Wygląd
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={mode === 'dark'}
                    onChange={toggleTheme}
                    color="primary"
                  />
                }
                label="Ciemny motyw"
              />
              {mode === 'dark' ? (
                <DarkMode sx={{ ml: 1, color: 'text.secondary' }} />
              ) : (
                <LightMode sx={{ ml: 1, color: 'text.secondary' }} />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Ciemny motyw zmniejsza zmęczenie oczu podczas pracy w słabo oświetlonym otoczeniu.
            </Typography>
          </WidgetCard>
        </Grid>
        */}
      </Grid>

      {/* Avatar Preview Dialog */}
      {/* <Dialog
        open={avatarPreviewOpen}
        onClose={handleCloseAvatarPreview}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Podgląd avatara</Typography>
          <Button
            onClick={handleCloseAvatarPreview}
            sx={{ minWidth: 'auto', p: 1 }}
          >
            <Close />
          </Button>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          {previewAvatar && (
            <>
              <Avatar
                src={previewAvatar.downloadUrl}
                sx={{
                  width: 200,
                  height: 200,
                  border: '4px solid',
                  borderColor: 'primary.main',
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={handleCloseAvatarPreview}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Anuluj
          </Button>
          <Button
            onClick={() => {
              if (previewAvatar) {
                handleAvatarSelect(previewAvatar);
                handleCloseAvatarPreview();
              }
            }}
            variant="contained"
            disabled={selectAvatarMutation.isPending}
            sx={{
              background: 'linear-gradient(to right, #10b981, #059669)',
              '&:hover': {
                background: 'linear-gradient(to right, #059669, #047857)',
              }
            }}
          >
            {selectAvatarMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Wybierz ten avatar'
            )}
          </Button>
        </DialogActions>
      </Dialog> */}

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MyAccountPage;